import React from "react";
import clsx from "clsx";
import Icon from "../btn/Icon";
import { More, AiLine } from "../../assets/svgs/icons";
import Action from "./Action";
import BubbleDropdown from "./Dropdown";
import ActionPopover from "./ActionPopover";
import Suggestion from "./Suggestion";
import MDEditor from "@uiw/react-md-editor";
import remarkGfm from "remark-gfm";
import remarkHtml from "remark-html";
import { useMediaMd } from "../../hooks/useMedia";

export type { default as ActionProps } from "./Action";
export type { default as BubbleDropdownProps } from "./Dropdown";
export type { default as ActionPopoverProps } from "./ActionPopover";
export type { default as SuggestionProps } from "./Suggestion";

interface BubbleProps {
  sender?: "user" | "agent-tu" | "agent-so";
  state?: "loading" | "completed";
  content?: string;
  suggestions?: string[];
  references?: Array<{
    source: string; // medium (Badge에 넘버링과 함께 표시)
    title: string; // name (Title에 표시)
    summary: string; // summary (Content에 표시)
    url?: string; // 클릭용 (선택)
  }>;
  showActions?: boolean;
  showSuggestions?: boolean;
  userEva?: {
    simple_eva: 0 | 1 | null;
    eva?: string;
  };
  onCopy?: () => void;
  onLike?: () => void;
  onDislike?: () => void;
  onFeedbackSubmit?: (feedback: string, type: "like" | "dislike") => void;
  onRegenerate?: () => void;
  onTts?: () => void;
  onSuggestionClick?: (suggestion: string) => void;
  className?: string;
  // TTS 관련
  isTtsSpeaking?: boolean;
  currentSpeakingMessageId?: string | null;
  responseId?: string;
}

const Bubble: React.FC<BubbleProps> = ({
  sender = "user",
  state = "completed",
  content = "",
  suggestions = [],
  references = [],
  showActions = false,
  showSuggestions = false,
  userEva,
  onCopy,
  onLike,
  onDislike,
  onFeedbackSubmit,
  onRegenerate,
  onTts,
  onSuggestionClick,
  className = "",
  isTtsSpeaking = false,
  currentSpeakingMessageId,
  responseId,
}) => {
  const isUser = sender === "user";
  const isAgent = sender === "agent-tu" || sender === "agent-so";
  const isLoading = state === "loading";
  const isMd = useMediaMd();

  // Loading placeholder text
  const getLoadingText = () => {
    if (sender === "agent-tu") return "입력하신 내용을 이해하고 있어요!";
    if (sender === "agent-so") return "면접 질문을 생각하고 있어요!";
    return "";
  };

  // Padding classes
  const padding = "p-5"; // 20px for both mobile and PC to match Figma

  // Background and border radius classes
  const getBubbleStyles = () => {
    if (isUser) {
      return {
        background: "bg-[#f7f7f7]",
        borderRadius: "rounded-tl-[20px] rounded-tr-[20px] rounded-bl-[20px]",
        textColor: "text-[#212121]",
        alignment: "ml-auto",
      };
    } else {
      return {
        background: "bg-[#f8f5fe]",
        borderRadius: "rounded-tr-[20px] rounded-br-[20px] rounded-bl-[20px]",
        textColor: isLoading ? "text-[#888888]" : "text-[#212121]",
        alignment: "mr-auto",
      };
    }
  };

  const bubbleStyles = getBubbleStyles();
  const fontWeight = isAgent && !isLoading ? "font-semibold" : "font-medium";

  // Icon badge for agent bubbles
  const IconBadge = () => {
    if (!isAgent) return null;

    return (
      <div
        className="absolute top-0 left-0 rounded-full bg-gradient-to-r from-[#cd3ec7] via-[#5328df] to-[#2d8efa] flex items-center justify-center w-8 h-8 md:w-10 md:h-10"
        style={{
          transform: "translate(-16px, -16px)",
        }}
      >
        {isLoading ? (
          <More
            width={16}
            height={16}
            fill="white"
            className="dot-animation md:w-5 md:h-5"
          />
        ) : (
          <AiLine
            width={16}
            height={16}
            fill="white"
            className="md:w-5 md:h-5"
          />
        )}
      </div>
    );
  };

  // Reference numbers in content
  const renderContentWithReferences = (text: string) => {
    if (references.length === 0) return text;

    let processedText = text;
    references.forEach((ref, index) => {
      const number = index + 1;
      const refPattern = new RegExp(`❲${number}❳`, "g");
      processedText = processedText.replace(
        refPattern,
        `<span class="text-[#c5abfa] font-medium">❲${number}❳</span>`
      );
    });

    return <span dangerouslySetInnerHTML={{ __html: processedText }} />;
  };

  return (
    <div className={clsx("relative", bubbleStyles.alignment, className)}>
      <IconBadge />

      <div
        className={clsx(
          bubbleStyles.background,
          bubbleStyles.borderRadius,
          padding,
          "flex",
          "flex-col",
          "gap-4",
          "mb-2"
        )}
      >
        <div
          className={clsx(
            "text-sm md:text-base leading-5 md:leading-6 tracking-[-0.28px] md:tracking-[-0.32px]",
            fontWeight,
            bubbleStyles.textColor
          )}
        >
          {isLoading && !content ? (
            // 로딩 중이고 content가 비어있을 때만 로딩 텍스트 표시 (타이핑 효과)
            <span className="typing-effect">{getLoadingText()}</span>
          ) : (
            // content가 있으면 표시 (로딩 중이어도 표시)
            <>
              {isAgent ? (
                <MDEditor.Markdown
                  source={content}
                  remarkPlugins={[remarkGfm, remarkHtml]}
                  className="wmde-markdown bg-transparent [&_code]:text-white [&_table]:text-white [&_th]:text-white [&_td]:text-white"
                  style={{
                    backgroundColor: "transparent",
                    color: "#212121",
                    fontFamily: "Pretendard",
                    fontSize: isMd ? "16px" : "14px",
                    fontStyle: "normal",
                    fontWeight: "500",
                    lineHeight: "20px",
                    letterSpacing: "-0.28px",
                  }}
                />
              ) : (
                <>
                  {references.length > 0
                    ? renderContentWithReferences(content)
                    : content}
                </>
              )}
            </>
          )}
        </div>

        {/* 참고 자료 보기 - agent에게만 표시 */}
        {isAgent && !isLoading && references.length > 0 && (
          <BubbleDropdown
            dropdown="off"
            items={references.map((ref, index) => ({
              badge: `${index + 1}. ${ref.source}`, // "1. 과학동아"
              title: ref.title, // "규칙 찾기의 탈인, 함수"
              content: ref.summary, // "요약 내용..."
              url: ref.url, // 클릭 시 새 탭에서 열기
            }))}
          />
        )}
      </div>

      {/* Action 컴포넌트는 bubble container 밖에 위치 - 8px 간격 */}
      {isAgent && showActions && !isLoading && (
        <Action
          visible={true}
          suggestion={showSuggestions}
          suggestions={suggestions}
          userEva={userEva}
          onCopy={onCopy}
          onLike={onLike}
          onDislike={onDislike}
          onFeedbackSubmit={onFeedbackSubmit}
          onRegenerate={onRegenerate}
          onTts={onTts}
          onSuggestionClick={onSuggestionClick}
          isTtsSpeaking={isTtsSpeaking}
          currentSpeakingMessageId={currentSpeakingMessageId}
          messageResponseId={responseId}
        />
      )}
    </div>
  );
};

export type { BubbleProps };

const BubbleWithComponents = Object.assign(Bubble, {
  Action,
  Dropdown: BubbleDropdown,
  ActionPopover,
  Suggestion,
});

export default BubbleWithComponents;

// 개별 import 지원
export { Action, BubbleDropdown, ActionPopover, Suggestion };
